import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomChip from '@ui-components/Chip/CustomChip';

import FilterIcon from '@assets/images/Filter.svg';
import SearchIcon from '@assets/images/search.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import { rowsPerPage } from '../constants';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import useDebouncing from '../components/useDebouncing';
import { getEndItem, getSortIcon, getStartItem } from '../components/Sorting';
import { useGetFacilityList } from '../../API/hooks/FacilityHook';
import {
  FACILITY_CLINICAIN_MAPPED_LIST_ROUTE_PATH,
  NEW_FACILITY_ROUTE_PATH,
} from '../../routers/routePaths';
import './facility.css';

import { StatusFilterSelector } from '../components/StatusFilterSelector';
import CustomPopover from '@qler-view/shared_lib/src/shared/lib/ui-components/Popover/CustomPopover';
import CustomDivider from '@qler-view/shared_lib/src/shared/lib/ui-components/Divider/CustomDivider';
import { useGetPartnerFacilityFilterData } from '../../API/hooks/PartnerFacilityFilterHook';

import FacilityTable from './facilityTable';
import { hasRole } from '../login/storageUtility';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import { useFilters } from '../../routers/FilterContext';
import { CustomBadge } from '@qler-view/shared_lib/src';
const FacilityList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId') ?? '0';
  const { filterContext, setFilterContext, getFilterCounts } = useFilters();
  const query = new URLSearchParams(location.search);
  const toast = query.get('toast');

  const { clinicianId } = useParams<{
    partnerId?: string;
    clinicianId?: string;
  }>();

  const [page, setPage] = useState(0);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );
  const [selectedStatusId, setSelectedStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const [filterStatusId, setFilterStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const [searchTermFacilityList, setSearchTermFacilityList] = useState<string>(
    filterContext.searchedValue
  );
  const debouncedSearchTerm = useDebouncing(searchTermFacilityList, 500);

  const inputRef = useRef<HTMLInputElement>(null);

  const { partnerId } = useParams();
  useEffect(() => {
    setSelectedStatusId(filterContext.statusIds);
    setSearchTermFacilityList(filterContext.searchedValue);
  }, [filterContext.statusIds, filterContext.searchedValue]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedStatusId]);

  const { FacilityListResponse, isLoading } = useGetFacilityList({
    userId: userId,
    partnerId: Number(partnerId),
    clinicianId: Number(clinicianId),
    page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
    statusId: selectedStatusId,
  });

  const FacilityListCount = FacilityListResponse?.totalRecords ?? 0;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus(); // Focus the input field
    }
  }, [searchTermFacilityList]);

  const [anchorFacilityListEl, setanchorFacilityListEl] =
    useState<HTMLElement | null>(null);
  const {
    PartnerFacilityFilterData,
    // isLoading: isLoadingFilters,
    refetch: refetchFilters,
  } = useGetPartnerFacilityFilterData();
  const handleFacilityListFilterClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setanchorFacilityListEl(event.currentTarget);
    refetchFilters();
  };
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const handleFacilityListSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove leading spaces
    value = value.replace(/^\s+/, '');

    // Replace multiple spaces with a single space
    value = value.replaceAll(/\s{2,}/g, ' ');

    setSearchTermFacilityList(value);
    setFilterContext({
      ...filterContext,
      searchedValue: value,
    });
  };

  const handleViewFacilityInfo = (facilityId: number) => {
    navigate('/ops/facility/details', { state: { facilityId: facilityId } });
  };

  const handleEditFacilityInfo = (facilityId: number) => {
    navigate(NEW_FACILITY_ROUTE_PATH + `/${facilityId}`, {
      replace: true,
      state: { isFacilityList: true },
    });
  };

  const handleFacilityClinicianMappingList = (
    facilityId: number,
    facilityName: string | null
  ) => {
    navigate(FACILITY_CLINICAIN_MAPPED_LIST_ROUTE_PATH + `/${facilityId}`, {
      state: { facilityName: facilityName, type: 'facility', ids: facilityId },
      replace: true,
    });
  };

  const handleApplyFilters = () => {
    setSelectedStatusId(filterStatusId);
    setFilterContext({
      ...filterContext,
      statusIds: filterStatusId,
    });
  };
  const handleStatusFilter = (statusIds: number[]) => {
    setFilterStatusId(statusIds);
  };

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (toast === 'success') {
      setMessage('Facility saved successfully!');
      setOpenToast(true);
    } else if (toast === 'update') {
      setMessage('Facility updated successfully!');
      setOpenToast(true);
    }
  }, [toast]);

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="toast-facility-list"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      <Box className="pt-20">
        <Grid2 container className="align-items-center">
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
            <CustomTypography
              id="page-title-facility-list"
              variant="h4"
              className="text-align-left"
            >
              Facilities
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <Box className="flex float-right">
              <Box>
                <CustomTextField
                  id="txt-facility-list"
                  name="search-facility-list"
                  placeholder="Search Facilities"
                  size="small"
                  type="text"
                  value={searchTermFacilityList}
                  onChange={handleFacilityListSearch}
                  startAdornment={
                    <CustomIconButton
                      id="search-icon"
                      size="small"
                      icon={<SearchIcon />}
                    />
                  }
                />
              </Box>
              <Box className="ml-20">
                <CustomBadge
                  id={`notification`}
                  badgeContent={getFilterCounts(filterContext).totalCount}
                  color="error"
                  max={99}
                >
                  <CustomChip
                    aria-haspopup="true"
                    onClick={(e) => {
                      handleFacilityListFilterClick(e);
                    }}
                    className="box-border2"
                    id="demo-chip"
                    label="Filters"
                    variant="outlined"
                    icon={<FilterIcon />}
                    iconPosition="left"
                  />
                </CustomBadge>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box className="ml-20">
                  <CustomButton
                    variant="contained"
                    id="btn-add-facility"
                    color="primary"
                    size="large"
                    onClick={() => {
                      navigate(NEW_FACILITY_ROUTE_PATH, { replace: true });
                    }}
                  >
                    Add Facility
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pt-20">
        <FacilityTable
          isLoading={isLoading}
          facility={FacilityListResponse?.facilityList ?? []}
          totalRecords={FacilityListCount}
          page={page}
          rowsPerPage={rowsPerPage}
          sortField={sortField ?? ''}
          sortType={sortType ?? 'desc'}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onEdit={handleEditFacilityInfo}
          onView={handleViewFacilityInfo}
          onFacilityCount={handleFacilityClinicianMappingList}
          getSortIcon={getSortIcon}
          getStartItem={getStartItem}
          getEndItem={getEndItem}
          action="list"
        />
      </Box>
      <Box>
        <CustomPopover
          open={Boolean(anchorFacilityListEl)}
          anchorEl={anchorFacilityListEl}
          onClose={() => setanchorFacilityListEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="mt-4"
        >
          <Box className="p-24">
            <Box className="text-align-left">
              <CustomTypography
                id="intake-form-filter-title"
                variant="h4"
                color="textPrimary"
              >
                Filters
              </CustomTypography>
              <Box className="pt-8">
                <CustomTypography
                  id="intake-form-filter-subtitle"
                  variant="body2"
                  color="textDisabled"
                >
                  Filter records by status
                </CustomTypography>
              </Box>
            </Box>
            <Box className="pt-16">
              <CustomDivider />
            </Box>
            <Box className="flex flex-column">
              <Grid2 container justifyContent="center" spacing={2}>
                <Grid2 size={{ xs: 12 }}>
                  <Box>
                    <Box className="flex-between-center pt-sm">
                      <Box>
                        <CustomTypography
                          id="facility-list-status-filter-title"
                          variant="body2"
                          color="textDisabled"
                        >
                          STATUS
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box className="pt-8 pb-8">
                      <StatusFilterSelector
                        data={PartnerFacilityFilterData}
                        value={filterStatusId}
                        isForConsult={false}
                        onChange={handleStatusFilter}
                      />
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box>
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <Box className="display-flex justify-end">
                    <Box className="pr-12">
                      <CustomButton
                        id="cancel-facility-user-button"
                        onClick={() => {
                          setanchorFacilityListEl(null);
                          setFilterStatusId([]);
                          setSelectedStatusId([]);
                          setFilterContext({
                            ...filterContext,
                            statusIds: [],
                          });
                        }}
                        variant="outlined"
                      >
                        Clear
                      </CustomButton>
                    </Box>
                    <Box>
                      <CustomButton
                        id="apply-facility-user-filter-button"
                        onClick={() => {
                          setanchorFacilityListEl(null);
                          handleApplyFilters();
                        }}
                        variant="contained"
                      >
                        Apply
                      </CustomButton>
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </Box>
        </CustomPopover>
      </Box>
    </Box>
  );
};

export default FacilityList;
